import React from 'react';
import { fromUnixTime, format } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';

// Components
import FavoriteIcon from '@icons/FavoriteIcon';
import ShareIcon from '@icons/ShareIcon';
import { ChipButtonLightGray } from '@styled';

// Functionality
import useSessionsDetailModal from '../../hooks/useSessionsDetailModal';
import { getTimeRangeText } from '@general/dates';
import getFavoriteHook from '@hooks/getFavoriteHook';

/**
 * Styled Components - Kept only for layout-specific spacing
 */
const ListItemContainer = styled.div`
    width: 100%;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const TrackChip = styled(ChipButtonLightGray)`
    color: #246EFF !important;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0.50px;
    padding: 8px 20px;
    height: 31px;
`;

/**
 * SessionsListItem
 * Renders a single session row with action buttons and deep-linking logic.
 */
const SessionsListItem = (props) => {
    if (!props || !props.id) return null;

    const {
        id, title, slug, sessionType, sessionStatus, sessionTracks = [],
        sessionStartUnix, sessionEndUnix, sessionRoom, event,
        isFavorite: initialIsFavorite, showFavoriteIcon = true,
    } = props;

    const history = useHistory();
    const location = useLocation();

    // Setup Favorite Functionality
    const useFavorite = getFavoriteHook({ type: 'session', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();

    const renderTime = () => {
        if (!sessionStartUnix || sessionStartUnix === 0) return 'Time TBD';
        return getTimeRangeText(fromUnixTime(sessionStartUnix), fromUnixTime(sessionEndUnix));
    };

    const { openModal: openSessionDetailModal } = useSessionsDetailModal({
        ...props,
        isFavorite,
        isLoadingFavorite,
        toggleFavorite: (e) => {
            // This allows the favorite button inside the modal to work
            if (e && typeof e.stopPropagation === 'function') {
                e.stopPropagation();
            }
            isFavorite ? removeFavorite(id) : saveFavorite(id);
        },
        onClose: () => {
            const currentPath = history.location.pathname.replace(/\/$/, '');
            if (props.slug && currentPath.endsWith(props.slug)) {
                const newPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
                history.push({ pathname: newPath, search: location.search });
            }
        },
    });

    const handleSessionClick = () => {
        const sessionDateStr = format(fromUnixTime(props.sessionStartUnix), 'yyyy-MM-dd');
        const basePath = location.pathname.split('/schedule')[0] + '/schedule';

        history.push({
            pathname: `${basePath}/${sessionDateStr}/${props.slug}`,
            search: location.search
        });

        openSessionDetailModal();
    };

    const toggleFavorite = async (e) => {
        // Check if e is a real event object before calling stopPropagation
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }

        isFavorite ? await removeFavorite(id) : await saveFavorite(id);
    };

    return (
        <ListItemContainer>
            {/* Divider using standard utility colors */}
            <div style={{ alignSelf: 'stretch', height: '1px', backgroundColor: '#D8D8D8' }} />

            <div className="d-flex justify-content-between align-items-start gap-4">
                {/* Info Column */}
                <div
                    className="flex-grow-2 d-flex flex-column gap-2"
                    onClick={handleSessionClick}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="session-title-text" style={{ fontSize: '24px' }}>
                        {title}
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                        {sessionTracks.map((track, idx) => (
                            <TrackChip key={`${id}-track-${idx}`} as="div">
                                {track}
                            </TrackChip>
                        ))}
                        {sessionStatus?.[0] === 'new' && (
                            <ChipButtonLightGray as="div">New Session</ChipButtonLightGray>
                        )}
                    </div>

                    <div className="session-meta-text">{renderTime()}</div>
                    {sessionRoom && <div className="session-meta-text">{sessionRoom}</div>}

                    {event?.audience_restriction && (
                        <div className="session-meta-text" style={{ fontStyle: 'italic' }}>
                            {event.audience_restriction}
                        </div>
                    )}
                </div>

                {/* Action Column */}
                <div className="d-flex align-items-center gap-2">
                    <ShareIcon title={title} url={event?.url} />
                    {showFavoriteIcon && (
                        <FavoriteIcon
                            id={id}
                            type="session"
                            isLoggedIn={true}
                            isFavorite={isFavorite}
                            isLoading={isLoadingFavorite}
                            onClick={toggleFavorite}
                        />
                    )}
                </div>
            </div>
        </ListItemContainer>
    );
};

SessionsListItem.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    slug: PropTypes.string,
    title: PropTypes.string.isRequired,
    sessionType: PropTypes.string,
    sessionStatus: PropTypes.arrayOf(PropTypes.string),
    sessionTracks: PropTypes.arrayOf(PropTypes.string),
    sessionStartUnix: PropTypes.number.isRequired,
    sessionEndUnix: PropTypes.number.isRequired,
    sessionRoom: PropTypes.string,
    event: PropTypes.object,
    isFavorite: PropTypes.bool,
    showFavoriteIcon: PropTypes.bool,
};

export default SessionsListItem;